import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/** A task a death makes (159, P-27): the client's case manager in the department, or the queue; dated from the death. */
export interface DeathAction {
  department: string;
  action_label: string;
  detail: string | null;
  /** Calendar days from the date of death; null for the next business day. */
  due_within_days: number | null;
  is_critical: boolean;
  assign_to: 'case_manager' | 'queue';
  due_rule: 'days' | 'next_business_day';
}

export interface DischargeEffect {
  effect: string;
  detail: string;
}

export interface Ending {
  legal_status: string | null;
  date_of_death: string | null;
  discharged_on: string | null;
  /** The guardianship ended by order (081), or on a date nobody knows. */
  guardianship_ended_on: string | null;
  guardianship_end_date_unknown: boolean;
  /**
   * The server decides. No death for someone deceased. A discharge for anyone
   * not yet discharged, a deceased client included (081).
   */
  may_record_death: boolean;
  may_record_discharge: boolean;
  /** 156 (A-#245): the death record whose date is not known, or null; and whether this reader may record its date. */
  undated_death_record_id: string | null;
  may_record_death_date: boolean;
  death_actions: DeathAction[];
  discharge_effects: DischargeEffect[];
  /** For a deceased client: the death rules still open, shown beside the discharge form, never a refusal. */
  open_death_rules: { label: string; due_date: string }[];
  /** A-#170: the discharge form's County, a choice from the catalogue (the code sent, the label shown). */
  counties: { code: string; label: string }[];
  /** A-#171: the client's latest live order, which the discharge form starts from; null with none on record. */
  latest_order: { court_name: string | null; county_code: string | null; court_index_number: string | null; judge_name: string | null } | null;
}

/**
 * What the Advanced control may offer, and what each ending would set in
 * motion — one request, made only when the control is opened.
 *
 * `enabled` is the whole point: this is never fetched for a client somebody is
 * simply reading. Opening the control is the first of the deliberate steps.
 */
export function useEnding(clientId: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'ending'],
    queryFn: () => apiFetch<Ending>(`/clients/${clientId}/ending`, getToken),
    ...queryDefaults,
    enabled,
  });
}

/**
 * Both endings invalidate everything about the client, and the lists they have
 * left: the visits board, the client list, every dashboard. A discharged or
 * deceased client is gone from all of them, and a stale row that still says
 * otherwise is the one thing worse than a slow refresh.
 */
function useEndingMutation<TBody>(clientId: string, path: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) =>
      apiFetch<{ client: { legal_status: string | null } }>(
        `/clients/${clientId}/${path}`,
        getToken,
        {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(body),
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
      void queryClient.invalidateQueries({ queryKey: ['work'] });
      // 156: a date of death moves the client out of triage's block and its new deadlines into triage
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export interface DeathInput {
  confirm_last_name: string;
  date_of_death: string;
  place_facility_id?: string;
  place_other?: string;
  informant_name?: string;
  informant_relationship?: string;
  informant_phone?: string;
  cause_of_death?: string;
  notes?: string;
}

export interface DischargeInput {
  /** A-#171: the client's surname typed back, checked by the server as a death's is. */
  confirm_last_name: string;
  signed_date: string;
  court_name: string;
  county: string;
  court_index_number?: string;
  judge_name?: string;
  notes?: string;
}

export const useRecordDeath = (clientId: string) =>
  useEndingMutation<DeathInput>(clientId, 'death');

export const useRecordDischarge = (clientId: string) =>
  useEndingMutation<DischargeInput>(clientId, 'discharge');

/** 156 (A-#245): the date of a death recorded with its date unknown, once. */
export interface DeathDateInput {
  death_record_id: string;
  date_of_death: string;
  confirm_last_name: string;
  reason: string;
}

export const useRecordDeathDate = (clientId: string) =>
  useEndingMutation<DeathDateInput>(clientId, 'death/date');
